import { StateCreator } from 'zustand';
import { message } from 'antd';
import type { StoreState } from '../types/types';
import type { Plot, PlotClue, PlotProgressPoint } from '../types/types';
import { api } from '../services/api';
import { logger } from '../services/logger.client';

export interface PlotState {
  plots: Plot[];
}
export interface PlotActions {
  setPlots: (plots: Plot[]) => void;
  addPlot: (plotName: string) => Promise<void>;
  updatePlot: (plot: Plot) => Promise<void>;
  deletePlot: (plotId: number) => Promise<void>;
  sortPlots: (plots: Plot[]) => Promise<void>;
  addClue: (plotId: number, text: string) => Promise<void>;
  updateClue: (plotId: number, clue: PlotClue, updates: Partial<PlotClue>) => Promise<void>;
  deleteClue: (plotId: number, clueId: number) => Promise<void>;
  addProgressPoint: (plotId: number, description: string) => Promise<void>;
  updateProgressPoint: (plotId: number, point: PlotProgressPoint, updates: Partial<PlotProgressPoint>) => Promise<void>;
  deleteProgressPoint: (plotId: number, pointId: number) => Promise<void>;
}

export type PlotSlice = PlotState & PlotActions;

export const createPlotActions: StateCreator<
  StoreState,
  [['zustand/immer', never]],
  [],
  { state: PlotState, actions: PlotActions }
> = (set, get) => ({
  state: {
    plots: [],
  },
  actions: {
    setPlots: (plots) => set(store => { store.session.plots.plots = plots; }),
    addPlot: async (plotName: string) => {
        const tempId = -Date.now();
        // Create a more complete temporary object to satisfy the Plot type
        const tempPlot = { 
          id: tempId, 
          plotName, 
          completed: false, 
          sortOrder: get().session.plots.plots.length,
          createdAt: new Date(),
          updatedAt: new Date(),
          imageId: null,
          clues: [],
          progressPoints: [],
        };
        set(state => { state.session.plots.plots.push(tempPlot as Plot); });

        try {
            const response = await api('/api/plots', { method: 'POST', body: JSON.stringify({ plotName }) });
            const newPlot = await response.json() as Plot;
            set(state => {
                const index = (state.session.plots.plots ?? []).findIndex((p: Plot) => p.id === tempId);
                if (index !== -1) state.session.plots.plots[index] = newPlot;
            });
            message.success('Plot added.');
        } catch (error) {
            logger.error(error, 'Failed to add plot');
            message.error('Failed to add plot.');
            set(state => { state.session.plots.plots = (state.session.plots.plots ?? []).filter((p: Plot) => p.id !== tempId); });
            throw error;
        }
    },
    updatePlot: async (plot: Plot) => {
        const originalPlots = get().session.plots.plots;
        const plotIndex = (originalPlots ?? []).findIndex(p => p.id === plot.id);
        if (plotIndex === -1) return;

        set(state => { state.session.plots.plots[plotIndex] = { ...state.session.plots.plots[plotIndex]!, ...plot }; });

        try {
            await api(`/api/plots/${plot.id}`, { method: 'PUT', body: JSON.stringify(plot) });
        } catch (error) {
            logger.error(error, 'Failed to update plot, rolling back.');
            message.error('Failed to update plot.');
            set(state => { state.session.plots.plots = originalPlots; });
            throw error;
        }
    },
    deletePlot: async (plotId: number) => {
        const originalPlots = get().session.plots.plots;
        set(state => { state.session.plots.plots = (originalPlots ?? []).filter((p: Plot) => p.id !== plotId); });

        try {
            await api(`/api/plots/${plotId}`, { method: 'DELETE' });
            message.success('Plot deleted.');
        } catch (error) {
            logger.error(error, 'Failed to delete plot, rolling back.');
            message.error('Failed to delete plot.');
            set(state => { state.session.plots.plots = originalPlots; });
            throw error;
        }
    },
    sortPlots: async (sortedPlots: Plot[]) => {
        const originalPlots = get().session.plots.plots;
        set(state => { state.session.plots.plots = sortedPlots; });

        try {
            const orderedIds = (sortedPlots ?? []).map(p => p.id);
            await api('/api/plots/sort', { method: 'PUT', body: JSON.stringify({ orderedIds }) });
        } catch (error) {
            logger.error(error, 'Failed to save plot order, rolling back.');
            message.error('Failed to save plot order.');
            set(state => { state.session.plots.plots = originalPlots; });
            throw error;
        }
    },
    addClue: async (plotId: number, text: string) => {
        const originalPlots = get().session.plots.plots;
        const tempClueId = -Date.now();
        set(state => {
            const plot = (state.session.plots.plots ?? []).find((p: Plot) => p.id === plotId);
            if (plot) {
                if (!plot.clues) plot.clues = [];
                plot.clues.push({ id: tempClueId, text, isFound: false, plotId });
            }
        });

        try {
            const response = await api(`/api/plots/${plotId}/clues`, { method: 'POST', body: JSON.stringify({ text }) });
            const newClue = await response.json() as PlotClue;
            set(state => {
                const plot = (state.session.plots.plots ?? []).find((p: Plot) => p.id === plotId);
                if (plot?.clues) {
                    const index = (plot.clues ?? []).findIndex((c: PlotClue) => c.id === tempClueId);
                    if (index !== -1) plot.clues[index] = newClue;
                }
            });
            message.success('Clue added.');
        } catch (error) {
            logger.error(error, 'Failed to add clue, rolling back.');
            message.error('Failed to add clue.');
            set(state => { state.session.plots.plots = originalPlots; });
            throw error;
        }
    },
    updateClue: async (plotId: number, clue: PlotClue, updates: Partial<PlotClue>) => {
        const originalPlots = get().session.plots.plots;
        set(state => {
            const plot = (state.session.plots.plots ?? []).find((p: Plot) => p.id === plotId);
            if (plot?.clues) {
                const clueIndex = (plot.clues ?? []).findIndex((c: PlotClue) => c.id === clue.id);
                if (clueIndex !== -1) {
                    plot.clues[clueIndex] = { ...plot.clues[clueIndex]!, ...updates };
                }
            }
        });

        try {
            await api(`/api/plots/clues/${clue.id}`, { method: 'PUT', body: JSON.stringify(updates) });
        } catch (err) {
            logger.error(err, 'Failed to update clue, rolling back.');
            set(state => { state.session.plots.plots = originalPlots; });
            throw err;
        }
    },
    deleteClue: async (plotId: number, clueId: number) => {
        const originalPlots = get().session.plots.plots;
        set(state => {
            const plot = (state.session.plots.plots ?? []).find((p: Plot) => p.id === plotId);
            if (plot?.clues) {
                plot.clues = (plot.clues ?? []).filter((c: PlotClue) => c.id !== clueId);
            }
        });

        try {
            await api(`/api/plots/clues/${clueId}`, { method: 'DELETE' });
        } catch (err) {
            logger.error(err, 'Failed to delete clue, rolling back.');
            set(state => { state.session.plots.plots = originalPlots; });
            throw err;
        }
    },
    addProgressPoint: async (plotId: number, description: string) => {
        const originalPlots = get().session.plots.plots;
        const tempPointId = -Date.now();
        set(state => {
            const plot = (state.session.plots.plots ?? []).find((p: Plot) => p.id === plotId);
            if (plot) {
                if (!plot.progressPoints) plot.progressPoints = [];
                plot.progressPoints.push({ id: tempPointId, description, isAchieved: false, plotId });
            }
        });

        try {
            const response = await api(`/api/plots/${plotId}/progress`, { method: 'POST', body: JSON.stringify({ description }) });
            const newPoint = await response.json() as PlotProgressPoint;
            set(state => {
                const plot = (state.session.plots.plots ?? []).find((p: Plot) => p.id === plotId);
                if (plot?.progressPoints) {
                    const index = (plot.progressPoints ?? []).findIndex((p: PlotProgressPoint) => p.id === tempPointId);
                    if (index !== -1) plot.progressPoints[index] = newPoint;
                }
            });
            message.success('Progress point added.');
        } catch (error) {
            logger.error(error, 'Failed to add progress point');
            message.error('Failed to add progress point.');
            set(state => { state.session.plots.plots = originalPlots; });
            throw error;
        }
    },
    updateProgressPoint: async (plotId: number, point: PlotProgressPoint, updates: Partial<PlotProgressPoint>) => {
        const originalPlots = get().session.plots.plots;
        set(state => {
            const plot = (state.session.plots.plots ?? []).find((p: Plot) => p.id === plotId);
            if (plot?.progressPoints) {
                const pointIndex = (plot.progressPoints ?? []).findIndex((p: PlotProgressPoint) => p.id === point.id);
                if (pointIndex !== -1) {
                    plot.progressPoints[pointIndex] = { ...plot.progressPoints[pointIndex]!, ...updates };
                }
            }
        });

        try {
            await api(`/api/plots/progress/${point.id}`, { method: 'PUT', body: JSON.stringify(updates) });
        } catch (err) {
            logger.error(err, 'Failed to update progress point, rolling back.');
            set(state => { state.session.plots.plots = originalPlots; });
            throw err;
        }
    },
    deleteProgressPoint: async (plotId: number, pointId: number) => {
        const originalPlots = get().session.plots.plots;
        set(state => {
            const plot = (state.session.plots.plots ?? []).find((p: Plot) => p.id === plotId);
            if (plot?.progressPoints) {
                plot.progressPoints = (plot.progressPoints ?? []).filter((p: PlotProgressPoint) => p.id !== pointId);
            }
        });
        try {
            await api(`/api/plots/progress/${pointId}`, { method: 'DELETE' });
        } catch (err) {
            logger.error(err, 'Failed to delete progress point, rolling back.');
            set(state => { state.session.plots.plots = originalPlots; });
            throw err;
        }
    },
  }
});